import { container } from '@/app/container';
import {
  TOTVS_INTEGRATOR_ROLES,
  TOTVS_INTEGRATOR_WRITE_ROLES,
} from '@/shared/guards/roles';
import type {
  PendingAlertStatus,
  PendingSource,
} from '@/modules/pending-alerts/domain/entities/pending-alert.entity';
import {
  TOTVS_OPERATIONAL_ALERT_ENTITY_TYPE,
  TOTVS_SYNC_TASK_ENTITY_TYPE,
} from '../domain/entities/totvs.entity';
import { TotvsRepositoryToken } from '../di/tokens';

const TOTVS_OPERATIONAL_ALERT_STATUS_LABEL: Record<string, string> = {
  OPEN: 'Aberto',
  RESOLVED: 'Resolvido',
  DISMISSED: 'Descartado',
};

/** Rótulos legíveis por `eventType` do audit log do TOTVS relevantes a um alerta. */
const TOTVS_ALERT_EVENT_LABEL: Record<string, string> = {
  alert_opened: 'Alerta aberto',
  alert_resolved: 'Alerta resolvido',
  alert_dismissed: 'Alerta descartado',
};

/**
 * Rótulos do vocabulário que o TOTVS de fato grava hoje —
 * `TotvsAlertType.SYNC_TASK_FAILED` e `TotvsEntityType` do monolith. Em pt-BR e
 * no mesmo vocabulário do resto do Integrador ("jobs de matrícula", não "sync
 * tasks"): `humanizeSnakeCase` devolvia "Sync task failed"/"Totvs sync task"
 * na tela, que é o nome da constante, não o nome da coisa.
 *
 * `type`/`entityType` continuam `string` aberta no contrato HTTP (o back pode
 * ganhar um tipo novo antes do front), então o que não estiver aqui ainda cai
 * no fallback abaixo em vez de sumir.
 */
const TOTVS_ALERT_TYPE_LABEL: Record<string, string> = {
  sync_task_failed: 'Job de matrícula falhou',
};

const TOTVS_ALERT_ENTITY_TYPE_LABEL: Record<string, string> = {
  [TOTVS_SYNC_TASK_ENTITY_TYPE]: 'Job de matrícula',
  totvs_product_link: 'De-para de produto',
  [TOTVS_OPERATIONAL_ALERT_ENTITY_TYPE]: 'Alerta operacional',
};

/**
 * Para onde vai quem clica no id da entidade afetada. Só entidade com tela
 * própria entra: `entityType` fora daqui deixa o id como texto puro, em vez de
 * um link para uma rota que não existe.
 */
const TOTVS_ENTITY_HREF: Record<string, (id: string) => string> = {
  [TOTVS_SYNC_TASK_ENTITY_TYPE]: (id) => `/totvs/jobs/${id}`,
};

/**
 * Rótulo legível a partir de uma chave snake_case ainda sem tradução (ex.:
 * `enrollment_job_failed` -> `Enrollment job failed`). Fallback para um valor
 * que o back passe a gravar antes de esta tela aprender o nome dele.
 */
function humanizeSnakeCase(value: string): string {
  const words = value.split('_').filter(Boolean);
  if (words.length === 0) return value;
  const [first, ...rest] = words as [string, ...string[]];
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

/**
 * Fonte de pendências do TOTVS para a Central de Pendências
 * (`modules/pending-alerts`). Consome o endpoint novo da Fase 5 do plano de
 * alertas compartilhados (`GET /admin/totvs/operational-alerts`), filtrado em
 * `OPEN` — maiúsculo, `OperationalAlertStatus` do shared; `status: 'open'` aqui
 * derrubava o filtro com 400 e a tela mostrava "nada pendente" mesmo com
 * alerta `OPEN` no banco. `requiredRoles: TOTVS_INTEGRATOR_ROLES` espelha o
 * gate `TOTVS_READ_ROLES` herdado da classe no `TotvsAdminController` — o
 * mesmo conjunto que já libera o menu/telas do Integrador TOTVS.
 */
export const totvsPendingSource: PendingSource = {
  key: 'totvs',
  label: 'TOTVS',
  requiredRoles: TOTVS_INTEGRATOR_ROLES,
  async fetch({ page, perPage, type }) {
    const repository = container.resolve(TotvsRepositoryToken);
    const response = await repository.listOperationalAlerts({
      page,
      perPage,
      status: 'OPEN',
      type: type || undefined,
      order: 'asc',
    });
    return {
      items: response.items.map((alert) => ({
        id: alert.id,
        sourceLabel: 'TOTVS',
        // `PendingAlertItem.status` do feed agregado é minúsculo; o TOTVS
        // responde maiúsculo (`OperationalAlertStatus` do shared) — normaliza aqui,
        // só para a exibição combinada.
        status: alert.status.toLowerCase() as PendingAlertStatus,
        statusLabel:
          TOTVS_OPERATIONAL_ALERT_STATUS_LABEL[alert.status] ?? alert.status,
        typeLabel:
          TOTVS_ALERT_TYPE_LABEL[alert.type] ?? humanizeSnakeCase(alert.type),
        entityTypeLabel:
          TOTVS_ALERT_ENTITY_TYPE_LABEL[alert.entityType] ??
          humanizeSnakeCase(alert.entityType),
        entityId: alert.entityId,
        entityHref: TOTVS_ENTITY_HREF[alert.entityType]?.(alert.entityId),
        inThisStatusSince: alert.inThisStatusSince,
      })),
      total: response.total,
    };
  },
  async fetchFacets() {
    const repository = container.resolve(TotvsRepositoryToken);
    const facets = await repository.listOperationalAlertFacets('OPEN');
    return facets.map(({ value, count }) => ({
      value,
      label: TOTVS_ALERT_TYPE_LABEL[value] ?? humanizeSnakeCase(value),
      count,
    }));
  },
  actions: {
    writeRoles: TOTVS_INTEGRATOR_WRITE_ROLES,
    async close(id, resolved, input) {
      const repository = container.resolve(TotvsRepositoryToken);
      if (resolved) {
        await repository.resolveOperationalAlert(id, input);
      } else {
        await repository.dismissOperationalAlert(id, input);
      }
    },
    // Sem `uploadAttachment`: o TOTVS ainda não tem endpoint de upload de
    // anexos (`POST admin/sales/attachments` é exclusivo do Sales) — a UI de
    // anexo da justificativa some para esta fonte em vez de oferecer um campo
    // que sempre falharia.
    async fetchHistory(id, { page, perPage }) {
      const repository = container.resolve(TotvsRepositoryToken);
      const response = await repository.getEntityEvents(
        TOTVS_OPERATIONAL_ALERT_ENTITY_TYPE,
        id,
        { page, perPage },
      );
      return {
        items: response.items.map((event) => ({
          id: event.id,
          eventLabel:
            TOTVS_ALERT_EVENT_LABEL[event.eventType] ?? event.eventType,
          isSystem: event.actorPlatformProfileId === null,
          actorName: event.actorName ?? null,
          reason: event.reason,
          createdAt: event.createdAt,
          payload: event.payload,
        })),
        total: response.total,
      };
    },
  },
};
